import { useQueries } from '@tanstack/react-query'
import { Icon } from '../ui/Icon'
import { ProjectAvatar } from '../identity/ProjectAvatar'
import { listBoards, type BoardInfo } from '../chatv1/transport'
import { chatv1SelfUser } from '../chatv1/auth'
import { isNews } from '../board/boardSeen'
import { MAIN_BOARD } from '../board/boardNav'
import { relativeAge } from '../ui/relativeTime'
import type { ProjectRow } from '../transport/api'
import { StagePage, type StageChrome } from './StagePage'

/**
 * EVERY WHITEBOARD, IN ONE PLACE — owner, 2026-11-01: Whiteboard 也要在 More 那邊也有一個地方可以來集中管理, 這樣使用者
 * 點進去, 就可以很容易的跳回他所需要去的 Whiteboard 的位置.
 *
 * The boards of every room this person is in, the one written to last first — with its room, when it last changed, and
 * the red dot the room's Whiteboard button shows when it changed since they looked (board/boardSeen). Pressing one
 * opens that room on that board. Read with the room's own query (['boards', conv], board/BoardMenu), so a board made
 * or renamed in the room is so here.
 */
export type BoardRow = { room: ProjectRow; board: BoardInfo; at: number; news: boolean }

/** `enabled: false` reads only what is already known — More's button counts without asking every room. */
export function useAllBoards(rooms: ProjectRow[], enabled = true) {
  const qs = useQueries({
    queries: rooms.map((r) => ({
      queryKey: ['boards', r.project_id],
      queryFn: async () => (await listBoards(r.project_id)).boards,
      enabled,
      staleTime: 15_000,
    })),
  })
  const me = chatv1SelfUser()
  const rows: BoardRow[] = []
  rooms.forEach((room, i) => {
    for (const b of qs[i]!.data ?? []) {
      // Every room has a first board before anyone draws: listed once it has been drawn on, or made by name.
      if (b.board === MAIN_BOARD && !b.rev) continue
      rows.push({ room, board: b, at: b.last_ts ?? b.created_ts, news: isNews(b, me) })
    }
  })
  rows.sort((a, b) => b.at - a.at)
  return { rows, loading: qs.some((q) => q.isLoading), failed: rooms.filter((_, i) => qs[i]!.isError) }
}

export function WhiteboardsPage({
  rooms,
  onOpenBoard,
  ...chrome
}: StageChrome & {
  rooms: ProjectRow[]
  onOpenBoard: (room: ProjectRow, board: string) => void
}) {
  const all = useAllBoards(rooms)
  const fresh = all.rows.filter((r) => r.news).length
  return (
    <StagePage
      {...chrome}
      title="Whiteboards"
      backLabel="Back"
      sub={all.rows.length ? `${all.rows.length} in ${new Set(all.rows.map((r) => r.room.project_id)).size} projects${fresh ? ` · ${fresh} changed` : ''}` : undefined}
      glyph={
        <span className="lp-chip lp-green" aria-hidden="true">
          <Icon name="schema" size={14} />
        </span>
      }
      className="more-page wb-page"
    >
      {all.loading && !all.rows.length && <p className="more-note">Reading your projects’ whiteboards…</p>}
      {!all.loading && !all.rows.length && (
        <p className="more-note">
          No whiteboards yet. Open a project and press its Whiteboard button — what you or its agents draw there is listed
          here.
        </p>
      )}
      {all.rows.length > 0 && (
        <ul className="wb-list" aria-label="Whiteboards">
          {all.rows.map(({ room, board, at, news }) => (
            <li key={`${room.project_id}:${board.board}`}>
              <button
                type="button"
                className="sb-row art-row wb-row"
                onClick={() => onOpenBoard(room, board.board)}
                title={`Open ${board.name} in ${room.name ?? 'the project'}`}
              >
                <ProjectAvatar projectId={room.project_id} label={room.name ?? room.project_id} size={26} />
                <span className="art-row-main">
                  <span className="art-row-name">
                    {board.name}
                    {news && <span className="wb-dot" aria-label="changed since you looked" />}
                  </span>
                  <span className="art-row-sub">
                    {room.name ?? 'Project'}
                    {at > 0 ? ` · ${board.last_ts ? 'changed' : 'made'} ${relativeAge((Date.now() - at) / 1000)}` : ''}
                  </span>
                </span>
                <Icon name="chevron_right" size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {all.failed.length > 0 && (
        <p className="more-note more-quiet">Could not read the whiteboards of: {all.failed.map((r) => r.name ?? r.project_id).join(', ')}.</p>
      )}
    </StagePage>
  )
}
